/**
 * Voice call with Mochi (design weco2/files-mochi/fm-voice.jsx): a centred
 * glass card with Mochi in the middle, a status pill, live captions and a
 * control dock (Mute · Captions · Type · End).
 *
 * Real behaviour, no scripted replies:
 * - the microphone is opened with echo cancellation; a level meter drives the
 *   glow, the listening bars and barge-in;
 * - a turn is recorded while you speak and ends after a short silence; the
 *   audio is transcribed by the assistant (`/voice/transcribe/`);
 * - the words go to the surface's own Mochi (`onTurn` — the Create page's
 *   brief, a file panel's run) and the reply is read out with the assistant's
 *   voice (`/voice/speak/`), captioned as it plays;
 * - with no microphone (or by choice) the same call works by typing.
 * The design's 2D character is replaced by the real 3D Mochi, whose mood
 * follows the call (listening / thinking / speaking).
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { ArrowUp, Captions, Keyboard, Mic, MicOff, PhoneOff, RotateCw, X } from "lucide-react";

import { transcribeAudio } from "../../../modules/assistant/api/voiceApi";
import { synthesizeSpeech } from "../../../modules/assistant/api/ttsApi";
import { useAssistantProfile } from "../../../modules/assistant/hooks/useAssistantProfile";
import { useSustainedMood } from "../publishers";
import type { MoodKind } from "../signals";
import { useAssistantConfig } from "../useAssistantConfig";
import { useFmScope } from "./fmScope";
import { FMScene, cx } from "./primitives";

export type VoiceState = "connecting" | "idle" | "listening" | "thinking" | "speaking" | "interrupted" | "muted" | "error";
export type VoiceErrorKind = "prompt" | "denied" | "unavailable" | "unsupported" | "offline" | "failed";

export interface FMVoiceProps {
  open: boolean;
  onClose: () => void;
  /** Where the call was started ("Create with Mochi", a file name…). */
  where?: string;
  /** One spoken or typed turn → Mochi's reply (plain text), or null for none. */
  onTurn: (text: string) => Promise<string | null | undefined>;
}

/** Loudness (0–1) above which the person is talking; higher while Mochi talks (echo). */
const TALK = 0.16;
const TALK_OVER = 0.42;
/** Silence that ends a turn. */
const END_OF_TURN_MS = 1100;
/** Shorter recordings are a cough, not a turn. */
const MIN_TURN_MS = 450;

const plain = (s: string) =>
  s
    .replace(/\[\[(?:file|folder):[^|\]]+\|([^|\]]+)(?:\|[^\]]+)?\]\]/g, "$1")
    .replace(/[*_#`>]+/g, "")
    .replace(/\s+/g, " ")
    .trim();

const reducedMotion = () => {
  try {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  } catch {
    return false;
  }
};

export function FMVoice({ open, onClose, where, onTurn }: FMVoiceProps) {
  if (!open) return null;
  return <VoiceCall onClose={onClose} where={where} onTurn={onTurn} />;
}

function VoiceCall({ onClose, where, onTurn }: Omit<FMVoiceProps, "open">) {
  const { t, i18n } = useTranslation();
  const { assistant_name: name } = useAssistantConfig();
  const { data: profile } = useAssistantProfile();
  const voice = (profile?.tts_voice as string) || "";
  useFmScope(true);

  const [state, setStateRaw] = useState<VoiceState>("connecting");
  const [errKind, setErrKind] = useState<VoiceErrorKind>("prompt");
  const [muted, setMuted] = useState(false);
  const [captionsOn, setCaptionsOn] = useState(true);
  const [typing, setTyping] = useState(false);
  const [textMode, setTextMode] = useState(false);
  const [typed, setTyped] = useState("");
  const [capUser, setCapUser] = useState("");
  const [capMochi, setCapMochi] = useState("");
  const [capFade, setCapFade] = useState(false);
  const [level, setLevel] = useState(0);
  const [mochiLevel, setMochiLevel] = useState(0);
  const [retry, setRetry] = useState(0);
  const [permState, setPermState] = useState("");

  const S = useRef({
    state: "connecting" as VoiceState,
    muted: false,
    open: true,
    stream: null as MediaStream | null,
    recorder: null as MediaRecorder | null,
    chunks: [] as Blob[],
    turnStart: 0,
    lastLoud: 0,
    loudSince: 0,
    audio: null as HTMLAudioElement | null,
    speakTok: 0,
    turnTok: 0,
    fadeT: 0 as ReturnType<typeof setTimeout> | 0,
  });
  const textRef = useRef(false);
  const onTurnRef = useRef(onTurn);
  onTurnRef.current = onTurn;
  const reduced = reducedMotion();

  const setState = useCallback((x: VoiceState) => {
    S.current.state = x;
    setStateRaw(x);
  }, []);

  const mood: MoodKind | null =
    state === "listening" ? "listening" : state === "thinking" ? "thinking" : state === "speaking" ? "speaking" : null;
  useSustainedMood(mood);

  const restState = () => (S.current.muted ? "muted" : "idle");

  // ---- speaking ----
  const stopSpeaking = useCallback(() => {
    S.current.speakTok += 1;
    const a = S.current.audio;
    if (a) {
      a.pause();
      a.src = "";
    }
    S.current.audio = null;
    setMochiLevel(0);
  }, []);

  const speak = useCallback(
    async (raw: string) => {
      const tok = ++S.current.speakTok;
      const text = plain(raw);
      if (!text) {
        setState(restState());
        return;
      }
      setState("speaking");
      setCapFade(false);
      setCapMochi("");
      const words = text.split(" ");
      const reveal = (n: number) => setCapMochi(words.slice(0, n).join(" "));
      const finish = () => {
        if (S.current.speakTok !== tok) return;
        setCapMochi(text);
        setMochiLevel(0);
        S.current.audio = null;
        setTimeout(() => {
          if (S.current.state !== "speaking" || S.current.speakTok !== tok) return;
          setState(restState());
          if (S.current.fadeT) clearTimeout(S.current.fadeT);
          S.current.fadeT = setTimeout(() => setCapFade(true), 1500);
        }, 300);
      };
      // Captions without audio: reveal word by word at a speaking pace.
      const simulate = () => {
        let w = 0;
        const timer = setInterval(() => {
          if (S.current.speakTok !== tok) return clearInterval(timer);
          w += 1;
          reveal(w);
          setMochiLevel(w % 2 ? 0.55 : 0.25);
          if (w >= words.length) {
            clearInterval(timer);
            finish();
          }
        }, reduced ? 220 : 260);
      };
      try {
        const lang = (i18n.language || "en").split("-")[0];
        const res = await synthesizeSpeech(text, { voice, locale: lang });
        const blob = await res.blob();
        if (S.current.speakTok !== tok) return;
        const url = URL.createObjectURL(blob);
        const audio = new Audio(url);
        S.current.audio = audio;
        let raf = 0;
        const tick = () => {
          if (S.current.audio !== audio) return;
          const d = audio.duration;
          if (d && Number.isFinite(d)) reveal(Math.ceil((audio.currentTime / d) * words.length));
          // A light, honest mouth: the reply is playing, not lip-synced.
          setMochiLevel(audio.paused ? 0 : 0.35 + Math.abs(Math.sin(audio.currentTime * 9)) * 0.45);
          raf = requestAnimationFrame(tick);
        };
        audio.onended = () => {
          cancelAnimationFrame(raf);
          URL.revokeObjectURL(url);
          finish();
        };
        audio.onerror = () => {
          cancelAnimationFrame(raf);
          URL.revokeObjectURL(url);
          if (S.current.speakTok === tok) simulate();
        };
        await audio.play();
        tick();
      } catch {
        if (S.current.speakTok === tok) simulate();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [voice, i18n.language, setState, reduced],
  );

  // ---- one turn ----
  const turn = useCallback(
    async (text: string) => {
      const said = text.trim();
      if (!said) {
        setState(restState());
        return;
      }
      const tok = ++S.current.turnTok;
      setState("thinking");
      setCapFade(false);
      setCapUser(said);
      setCapMochi("");
      let reply: string | null | undefined = null;
      try {
        reply = await onTurnRef.current(said);
      } catch (e) {
        reply = (e as Error)?.message || t("filesMochi.voice.replyFailed", { defaultValue: "Something went wrong on my side. Try again?" });
      }
      if (!S.current.open || S.current.turnTok !== tok) return;
      if (reply && reply.trim()) void speak(reply);
      else setState(restState());
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [speak, setState, t],
  );

  const transcribeTurn = useCallback(
    async (blob: Blob) => {
      setState("thinking");
      try {
        const lang = (i18n.language || "").split("-")[0] || undefined;
        const text = await transcribeAudio(blob, lang);
        if (!S.current.open) return;
        if (text) await turn(text);
        else setState(restState());
      } catch {
        if (!S.current.open) return;
        setCapUser("");
        setCapMochi(t("filesMochi.voice.notHeard", { defaultValue: "Sorry, I didn’t catch that. Could you say it again?" }));
        setState(restState());
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [turn, setState, i18n.language, t],
  );

  // ---- microphone: level meter, turn recording, barge-in ----
  useEffect(() => {
    S.current.open = true;
    setState("connecting");
    setCapUser("");
    setCapMochi("");
    setMuted(false);
    S.current.muted = false;
    try {
      navigator.permissions
        ?.query({ name: "microphone" as PermissionName })
        .then((r) => {
          setPermState(r.state);
          r.onchange = () => setPermState(r.state);
        })
        .catch(() => {});
    } catch {
      /* permissions API not available */
    }
    if (navigator.onLine === false) {
      setErrKind("offline");
      setState("error");
      return () => {
        S.current.open = false;
      };
    }
    if (textRef.current) {
      setState("idle");
      return () => {
        S.current.open = false;
        stopSpeaking();
      };
    }
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") {
      setErrKind("unsupported");
      setState("error");
      return () => {
        S.current.open = false;
      };
    }
    let alive = true;
    let raf = 0;
    let ctx: AudioContext | null = null;
    (async () => {
      let stream: MediaStream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true } });
      } catch (e) {
        if (!alive) return;
        const nm = (e as { name?: string })?.name;
        if (nm === "NotFoundError" || nm === "OverconstrainedError" || nm === "NotReadableError") setErrKind("unavailable");
        else if (nm === "SecurityError") setErrKind("unsupported");
        else {
          let denied = false;
          try {
            const r = await navigator.permissions.query({ name: "microphone" as PermissionName });
            denied = r.state === "denied";
          } catch {
            /* unknown */
          }
          setErrKind(denied ? "denied" : "prompt");
        }
        if (alive) setState("error");
        return;
      }
      if (!alive) {
        stream.getTracks().forEach((tr) => tr.stop());
        return;
      }
      S.current.stream = stream;
      const AC = window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      let analyser: AnalyserNode | null = null;
      if (AC) {
        ctx = new AC();
        analyser = ctx.createAnalyser();
        analyser.fftSize = 512;
        ctx.createMediaStreamSource(stream).connect(analyser);
      }
      const buf = new Uint8Array(analyser ? analyser.fftSize : 0);
      let lvl = 0;

      const startRecording = () => {
        if (S.current.recorder) return;
        try {
          const rec = new MediaRecorder(stream);
          S.current.chunks = [];
          rec.ondataavailable = (ev) => {
            if (ev.data && ev.data.size) S.current.chunks.push(ev.data);
          };
          rec.start();
          S.current.recorder = rec;
          S.current.turnStart = performance.now();
        } catch {
          /* recorder refused: typing still works */
        }
      };
      const endRecording = (keep: boolean) => {
        const rec = S.current.recorder;
        S.current.recorder = null;
        if (!rec || rec.state === "inactive") return;
        const long = performance.now() - S.current.turnStart >= MIN_TURN_MS;
        rec.onstop = () => {
          const blob = new Blob(S.current.chunks, { type: rec.mimeType || "audio/webm" });
          S.current.chunks = [];
          if (keep && long && blob.size > 0 && S.current.open) void transcribeTurn(blob);
          else if (S.current.state === "listening") setState(restState());
        };
        rec.stop();
      };

      const tick = () => {
        if (analyser) {
          analyser.getByteTimeDomainData(buf);
          let sum = 0;
          for (let i = 0; i < buf.length; i++) {
            const v = (buf[i] - 128) / 128;
            sum += v * v;
          }
          lvl = lvl * 0.7 + Math.min(1, Math.sqrt(sum / buf.length) * 7) * 0.3;
        }
        const L = S.current.muted ? 0 : lvl;
        setLevel(Math.round(L * 100));
        const now = performance.now();
        const s = S.current.state;
        if (L > (s === "speaking" ? TALK_OVER : TALK)) {
          if (s === "speaking") {
            // Barge-in: talking over Mochi stops it and starts a new turn.
            if (!S.current.loudSince) S.current.loudSince = now;
            if (now - S.current.loudSince > 220) {
              S.current.loudSince = 0;
              stopSpeaking();
              setState("interrupted");
              setTimeout(() => {
                if (S.current.state === "interrupted") setState("listening");
              }, 150);
              startRecording();
            }
          } else if (s === "idle" || s === "interrupted") {
            setState("listening");
            setCapFade(false);
            setCapUser("");
            setCapMochi("");
            startRecording();
          }
          S.current.lastLoud = now;
        } else {
          S.current.loudSince = 0;
          if (s === "listening" && S.current.lastLoud && now - S.current.lastLoud > END_OF_TURN_MS) {
            S.current.lastLoud = 0;
            endRecording(true);
          }
        }
        raf = requestAnimationFrame(tick);
      };
      tick();
      if (S.current.state === "connecting") setState("idle");
    })();
    return () => {
      alive = false;
      S.current.open = false;
      cancelAnimationFrame(raf);
      stopSpeaking();
      const rec = S.current.recorder;
      S.current.recorder = null;
      if (rec && rec.state !== "inactive") {
        rec.onstop = null;
        try {
          rec.stop();
        } catch {
          /* already stopped */
        }
      }
      S.current.stream?.getTracks().forEach((tr) => tr.stop());
      S.current.stream = null;
      void ctx?.close().catch(() => {});
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [retry]);

  // Mute: the track stays open (no permission prompt again), its audio is silenced.
  useEffect(() => {
    S.current.muted = muted;
    S.current.stream?.getAudioTracks().forEach((tr) => {
      tr.enabled = !muted;
    });
    if (muted && S.current.state !== "speaking" && S.current.state !== "error") {
      const rec = S.current.recorder;
      S.current.recorder = null;
      if (rec && rec.state !== "inactive") {
        rec.onstop = null;
        rec.stop();
      }
      setState("muted");
    } else if (!muted && S.current.state === "muted") setState("idle");
  }, [muted, setState]);

  // Offline / back online.
  useEffect(() => {
    const off = () => {
      if (textRef.current) return;
      setErrKind("offline");
      stopSpeaking();
      setState("error");
    };
    const on = () => {
      if (S.current.state === "error") setRetry((k) => k + 1);
    };
    window.addEventListener("offline", off);
    window.addEventListener("online", on);
    return () => {
      window.removeEventListener("offline", off);
      window.removeEventListener("online", on);
    };
  }, [setState, stopSpeaking]);

  // Esc ends the call; M mutes.
  useEffect(() => {
    const k = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement | null)?.tagName;
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose();
      } else if ((e.key === "m" || e.key === "M") && !typing && tag !== "INPUT" && tag !== "TEXTAREA") setMuted((x) => !x);
    };
    document.addEventListener("keydown", k, true);
    return () => document.removeEventListener("keydown", k, true);
  }, [onClose, typing]);

  useEffect(
    () => () => {
      if (S.current.fadeT) clearTimeout(S.current.fadeT);
    },
    [],
  );

  const sendTyped = () => {
    const v = typed.trim();
    if (!v) return;
    setTyped("");
    if (S.current.state === "speaking") stopSpeaking();
    void turn(v);
  };

  const isErr = state === "error";
  const offline = isErr && errKind === "offline";
  const glow = isErr ? (offline ? "error" : "idle") : state === "listening" ? "user" : state === "speaking" ? "mochi" : state;
  const amp = state === "listening" ? level / 100 : state === "speaking" ? mochiLevel : 0;
  const micIssue = !offline && (isErr || textMode);

  const ERR: Record<VoiceErrorKind, { h: string; p: string; a: string | null }> = {
    prompt: {
      h: t("filesMochi.voice.err.prompt.h", "Let’s get you talking"),
      p: t("filesMochi.voice.err.prompt.p", {
        defaultValue: "Allow microphone access to start a voice conversation with {{name}}, or type your message instead.",
        name,
      }),
      a: t("filesMochi.voice.err.prompt.a", "Enable microphone"),
    },
    denied: {
      h: t("filesMochi.voice.err.denied.h", "Microphone is blocked"),
      p: t(
        "filesMochi.voice.err.denied.p",
        "Your browser is blocking the microphone for this site. Click the lock icon in the address bar, set Microphone to Allow, then try again.",
      ),
      a: t("filesMochi.voice.err.denied.a", "Try again"),
    },
    unavailable: {
      h: t("filesMochi.voice.err.unavailable.h", "No microphone found"),
      p: t("filesMochi.voice.err.unavailable.p", "Connect a microphone, then check again. You can also type your message instead."),
      a: t("filesMochi.voice.err.unavailable.a", "Check again"),
    },
    unsupported: {
      h: t("filesMochi.voice.err.unsupported.h", "Voice isn’t available here"),
      p: t("filesMochi.voice.err.unsupported.p", {
        defaultValue: "This browser or connection can’t use the microphone. You can still type to {{name}}.",
        name,
      }),
      a: null,
    },
    offline: {
      h: t("filesMochi.voice.err.offline.h", "You’re offline"),
      p: t("filesMochi.voice.err.offline.p", {
        defaultValue: "Voice needs an internet connection. {{name}} will reconnect on its own when you’re back, or you can type for now.",
        name,
      }),
      a: t("filesMochi.voice.err.offline.a", "Try again"),
    },
    failed: {
      h: t("filesMochi.voice.err.failed.h", "Voice didn’t start"),
      p: t("filesMochi.voice.err.failed.p", "Something went wrong starting the call. Try again, or type instead."),
      a: t("filesMochi.voice.err.failed.a", "Try again"),
    },
  };
  const err = ERR[errKind];

  const LABEL: Record<VoiceState, string> = {
    connecting: t("filesMochi.voice.state.connecting", { defaultValue: "Connecting to {{name}}…", name }),
    idle: t("filesMochi.voice.state.idle", { defaultValue: "{{name}} is here — just start talking", name }),
    listening: t("filesMochi.voice.state.listening", { defaultValue: "{{name}} is listening", name }),
    thinking: t("filesMochi.voice.state.thinking", { defaultValue: "{{name}} is thinking", name }),
    speaking: t("filesMochi.voice.state.speaking", { defaultValue: "{{name}} is speaking", name }),
    interrupted: t("filesMochi.voice.state.interrupted", { defaultValue: "{{name}} stopped to listen", name }),
    muted: t("filesMochi.voice.state.muted", "Your mic is muted"),
    error: t("filesMochi.voice.state.error", { defaultValue: "{{name}} can’t hear you", name }),
  };
  const hint: Record<VoiceState, string> = {
    connecting:
      permState === "prompt"
        ? t("filesMochi.voice.hint.waitingPermission", "Waiting for microphone permission…")
        : t("filesMochi.voice.hint.connecting", "Connecting…"),
    idle: textMode ? t("filesMochi.voice.hint.typingMode", "Typing mode · mic is off") : t("filesMochi.voice.hint.idle", "Just start talking"),
    listening: t("filesMochi.voice.hint.listening", "Listening…"),
    thinking: t("filesMochi.voice.hint.thinking", "Thinking…"),
    speaking: t("filesMochi.voice.hint.speaking", "Speaking"),
    interrupted: t("filesMochi.voice.hint.interrupted", "Go ahead"),
    muted: t("filesMochi.voice.hint.muted", "Mic is muted"),
    error: "",
  };

  // Typing mode closes the microphone (the call restarts without it).
  const goText = () => {
    textRef.current = true;
    setTextMode(true);
    setTyping(true);
    setRetry((k) => k + 1);
  };
  const retryMic = () => {
    textRef.current = false;
    setTextMode(false);
    setRetry((k) => k + 1);
  };

  const pillLabel = offline
    ? t("filesMochi.voice.pill.offline", "Offline")
    : state === "connecting"
      ? t("filesMochi.voice.pill.connecting", "Connecting")
      : t("filesMochi.voice.pill.voice", "Voice");
  const micChip = textMode
    ? t("filesMochi.voice.mic.off", "Mic off")
    : errKind === "denied"
      ? t("filesMochi.voice.mic.blocked", "Mic blocked")
      : errKind === "unavailable"
        ? t("filesMochi.voice.mic.none", "No mic")
        : t("filesMochi.voice.mic.notAllowed", "Mic not allowed");

  return createPortal(
    <div
      className={cx("fm fmv", `glow-${glow}`, `is-${state}`, reduced && "is-reduced")}
      role="dialog"
      aria-modal="true"
      aria-label={t("filesMochi.voice.dialog", { defaultValue: "Voice chat with {{name}}", name })}
      data-fm-nokeys=""
      style={{ ["--amp" as string]: amp.toFixed(2) }}
    >
      <div className="fmv-bg" aria-hidden="true" onClick={onClose} />
      <div className="fmv-card">
        <span className="fmv-glow" aria-hidden="true" />
        <div className="fmv-top">
          <div className="fmv-tags">
            <span className={cx("fmv-pill", offline && "is-off", state === "connecting" && "is-wait")}>
              <span className="fmv-dot" />
              {pillLabel}
              {where ? <span className="fmv-where">{where}</span> : null}
            </span>
            {micIssue && (
              <span className="fmv-micchip">
                <MicOff size={13} aria-hidden />
                {micChip}
              </span>
            )}
          </div>
          <button
            type="button"
            className="fmv-close"
            onClick={onClose}
            aria-label={t("filesMochi.voice.close", "Close voice chat")}
          >
            <X size={18} aria-hidden />
          </button>
        </div>
        <div className="fmv-stage">
          <div className="fmv-char-wrap">
            <FMScene size={0} />
          </div>
          <p className="sr-only" aria-live="polite">
            {isErr ? `${err.h}. ${err.p}` : LABEL[state]}
          </p>
          {isErr ? (
            <div className="fmv-err" role="group" aria-label={err.h}>
              <h2>{err.h}</h2>
              <p>{err.p}</p>
              <div className="fmv-err-actions">
                {err.a && (
                  <button type="button" className="fmv-allow" onClick={retryMic}>
                    {offline ? <RotateCw size={18} aria-hidden /> : <Mic size={18} aria-hidden />}
                    {err.a}
                  </button>
                )}
                <button type="button" className="fmv-alt" onClick={goText}>
                  <Keyboard size={18} aria-hidden />
                  {t("filesMochi.voice.continueText", "Continue with text")}
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className="fmv-status" aria-hidden="true">
                {state === "thinking" ? (
                  <span className="fmv-think">
                    <i />
                    <i />
                    <i />
                  </span>
                ) : state === "listening" ? (
                  <span className="fmv-bars">
                    {[0.6, 1, 0.75, 0.9, 0.55].map((f, i) => (
                      <i key={i} style={{ transform: `scaleY(${(0.25 + Math.min(1, level / 60) * f).toFixed(2)})` }} />
                    ))}
                  </span>
                ) : state === "muted" ? (
                  <MicOff size={16} />
                ) : null}
                <span>{hint[state]}</span>
              </div>
              {captionsOn && (
                <div className={cx("fmv-caps", capFade && "is-fade")} aria-live="polite" data-testid="fmv-caps">
                  {capUser && (
                    <p className="u" dir="auto">
                      {capUser}
                    </p>
                  )}
                  {capMochi && (
                    <p className="m" dir="auto">
                      {capMochi}
                    </p>
                  )}
                </div>
              )}
            </>
          )}
        </div>
        {typing && (
          <div className="fmv-type fm-fade">
            <input
              autoFocus
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.nativeEvent.isComposing) sendTyped();
                if (e.key === "Escape") {
                  e.stopPropagation();
                  setTyping(false);
                }
              }}
              placeholder={t("filesMochi.voice.typePlaceholder", { defaultValue: "Type to {{name}}…", name })}
              aria-label={t("filesMochi.voice.typePlaceholder", { defaultValue: "Type to {{name}}…", name })}
              dir="auto"
            />
            <button
              type="button"
              onClick={sendTyped}
              className={cx("fm-send", typed.trim() && "is-on")}
              aria-label={t("filesMochi.composer.send", "Send")}
            >
              <ArrowUp size={16} aria-hidden />
            </button>
          </div>
        )}
        <div className="fmv-dock" role="toolbar" aria-label={t("filesMochi.voice.controls", "Voice controls")}>
          <div className="fmv-tiles">
            {textMode || isErr ? (
              <button
                type="button"
                className="fmv-ctl"
                onClick={retryMic}
                aria-label={t("filesMochi.voice.enableMicLabel", "Enable microphone")}
                disabled={offline || errKind === "unsupported"}
              >
                <Mic size={20} aria-hidden />
                <span>{t("filesMochi.voice.enableMic", "Enable mic")}</span>
              </button>
            ) : (
              <button
                type="button"
                className={cx("fmv-ctl", muted && "is-on")}
                onClick={() => setMuted((m) => !m)}
                aria-pressed={muted}
                aria-label={
                  muted ? t("filesMochi.voice.unmuteLabel", "Unmute microphone") : t("filesMochi.voice.muteLabel", "Mute microphone")
                }
              >
                {muted ? <MicOff size={20} aria-hidden /> : <Mic size={20} aria-hidden />}
                <span>{muted ? t("filesMochi.voice.unmute", "Unmute") : t("filesMochi.voice.mute", "Mute")}</span>
              </button>
            )}
            <button
              type="button"
              className={cx("fmv-ctl", captionsOn && "is-sel")}
              onClick={() => setCaptionsOn((c) => !c)}
              aria-pressed={captionsOn}
              aria-label={
                captionsOn
                  ? t("filesMochi.voice.captionsOffLabel", "Turn captions off")
                  : t("filesMochi.voice.captionsOnLabel", "Turn captions on")
              }
            >
              <Captions size={20} aria-hidden />
              <span>{t("filesMochi.voice.captions", "Captions")}</span>
            </button>
            <button
              type="button"
              className={cx("fmv-ctl", typing && "is-sel")}
              onClick={() => setTyping((v) => !v)}
              aria-pressed={typing}
              aria-label={typing ? t("filesMochi.voice.hideTypeLabel", "Hide text input") : t("filesMochi.voice.typeLabel", "Type instead")}
            >
              <Keyboard size={20} aria-hidden />
              <span>{t("filesMochi.voice.type", "Type")}</span>
            </button>
          </div>
          <button type="button" className="fmv-end" onClick={onClose} aria-label={t("filesMochi.voice.endLabel", "End voice chat")}>
            <PhoneOff size={18} aria-hidden />
            <span>{t("filesMochi.voice.end", "End")}</span>
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
